import React from 'react';
import { ExternalBarcodeLookupResponse, Product } from 'utils/grocyApi.ts';
import ParentProductSelector from './ParentProductSelector.tsx';
import { CreationMode } from '../../types/purchase.ts';
import { UnknownProductSuggestionResult } from 'utils/unknownProductSuggestion.ts';

interface ParentOrLinkSelectorProps {
  unknownProduct?: ExternalBarcodeLookupResponse;
  creationMode: CreationMode;
  onChangeCreationMode: (mode: CreationMode) => void;

  parentProductId: number | null;
  onParentProductSelect: (product: Product) => void;
  onParentProductClear: () => void;
  parentProductSearchTerm: string;
  onParentProductSearchTermChange: (term: string) => void;

  linkProductId: number | null;
  onLinkProductSelect: (product: Product) => void;
  onLinkProductClear: () => void;
  linkProductSearchTerm: string;
  onLinkProductSearchTermChange: (term: string) => void;

  products?: Product[];
  aiLoading?: boolean;
  aiSuggestion?: UnknownProductSuggestionResult | null;
  aiSuggestionProductName?: string | null;
  aiDismissed?: boolean;
  onDismissAISuggestion?: () => void;
}

const ParentOrLinkSelector: React.FC<ParentOrLinkSelectorProps> = ({
  unknownProduct,
  creationMode,
  onChangeCreationMode,
  parentProductId,
  onParentProductSelect,
  onParentProductClear,
  parentProductSearchTerm,
  onParentProductSearchTermChange,
  linkProductId,
  onLinkProductSelect,
  onLinkProductClear,
  linkProductSearchTerm,
  onLinkProductSearchTermChange,
  products,
  aiLoading = false,
  aiSuggestion = null,
  aiSuggestionProductName = null,
  aiDismissed = false,
  onDismissAISuggestion,
}) => {
  // Define content items that should be shown
  const contentItems: Array<{ key: string; content: React.ReactElement }> = [];
  
  if (unknownProduct) {
    contentItems.push({
      key: 'toggle',
      content: (
        <div className="purchase-controls__toggle">
          <label className={`purchase-controls__toggle-option ${creationMode === CreationMode.Create ? 'active' : ''}`}>
            <input
              type="radio"
              name="unknown-mode"
              checked={creationMode === CreationMode.Create}
              onChange={() => onChangeCreationMode(CreationMode.Create)}
            />
            New
          </label>
          <label className={`purchase-controls__toggle-option ${creationMode === CreationMode.Parent ? 'active' : ''}`}>
            <input
              type="radio"
              name="unknown-mode"
              checked={creationMode === CreationMode.Parent}
              onChange={() => onChangeCreationMode(CreationMode.Parent)}
            />
            Parent
          </label>
          <label className={`purchase-controls__toggle-option ${creationMode === CreationMode.Link ? 'active' : ''}`}>
            <input
              type="radio"
              name="unknown-mode"
              checked={creationMode === CreationMode.Link}
              onChange={() => onChangeCreationMode(CreationMode.Link)}
            />
            Link
          </label>
        </div>
      )
    });
  }

  if (unknownProduct && aiLoading) {
    contentItems.push({
      key: 'ai-loading',
      content: (
        <div className="purchase-controls__ai-loading">
          AI checking your shopping list and catalog for a likely match...
        </div>
      )
    });
  }

  if (unknownProduct && aiSuggestion && !aiDismissed) {
    const label = aiSuggestion.mode === CreationMode.Parent
      ? 'Parent'
      : aiSuggestion.mode === CreationMode.Link
      ? 'Link'
      : 'New';

    const targetText = aiSuggestionProductName ? ` to ${aiSuggestionProductName}` : '';

    contentItems.push({
      key: 'ai-suggestion',
      content: (
        <div className="purchase-controls__ai-suggestion">
          <div className="purchase-controls__ai-suggestion-copy">
            <strong>{`AI suggested: ${label}${targetText}`}</strong>
            {aiSuggestion.reason && (
              <span>{aiSuggestion.reason}</span>
            )}
          </div>
          {onDismissAISuggestion && (
            <button
              type="button"
              className="purchase-controls__ai-dismiss"
              onClick={onDismissAISuggestion}
            >
              Dismiss
            </button>
          )}
        </div>
      )
    });
  }

  if (creationMode !== CreationMode.Create) {
    contentItems.push({
      key: 'product-picker',
      content: (
        <div className="purchase-controls__parent-product-picker">
          {creationMode === CreationMode.Parent && (
            <ParentProductSelector
              parentProductId={parentProductId}
              onSelect={onParentProductSelect}
              onClear={onParentProductClear}
              searchTerm={parentProductSearchTerm}
              onSearchTermChange={onParentProductSearchTermChange}
              products={products}
            />
          )}

          {unknownProduct && creationMode === CreationMode.Link && (
            <ParentProductSelector
              parentProductId={linkProductId}
              onSelect={onLinkProductSelect}
              onClear={onLinkProductClear}
              searchTerm={linkProductSearchTerm}
              onSearchTermChange={onLinkProductSearchTermChange}
              products={products}
            />
          )}
        </div>
      )
    });
  }

  return (
    <div className="purchase-controls__field purchase-controls__field--vertical">
      {contentItems.length > 0 && (
        <div className="purchase-controls__parent-group">
          {contentItems.map(item => (
            <React.Fragment key={item.key}>
              {item.content}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParentOrLinkSelector;

